/* eslint-disable  @typescript-eslint/no-explicit-any */

import React, { useState, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import { authAtom } from '../../states/usuarios/AuthState';
import { generatePDFFromHTML, previewCurriculumHTML } from '../../utils/pdfGenerator';
import { useCurriculoActions } from '../../states/curriculo/curriculo.actions';
import {
  curriculoAnalysisAtom,
  curriculoSuggestionsAtom,
  curriculoLoadingAtom,
  curriculoProcessingAtom,
} from '../../states/curriculo/CurriculoState';
import { useTranslation } from '../../commons/useTranslasion';
import { toast } from 'react-toastify';
import { SalvarVersaoCurriculoModal } from '../../components/SalvarVersaoCurriculoModal/SalvarVersaoCurriculoModal';
import InputArquivo from '../../components/InputArquivo/InputArquivo';
import { TextFieldStato } from '../../commons/styleds/textFields';
import { HistoricoCurriculos } from '../../components/HistoricosCurriculos/HistoricoCurriculos';
import { useComandaActions } from '../../states/comandas/comanda.actions';
import { SelectStato } from '../../commons/styleds/selects';

interface ComandaTemp {
  id: number;
  nomeContato: string;
  emailContato: string;
  cpfContato: string;
  idContato: number;
  plano: string;
  status: string;
  modeloComercial: string;
  dataInicio: string;
  dataFim: string;
  dataExtensao: string;
}

const NovoCurriculoIAComponent: React.FC = () => {
  const { translate } = useTranslation();
  const auth = useRecoilValue(authAtom);
  const curriculoActions = useCurriculoActions();
  const comandaActions = useComandaActions();

  // Estados do Recoil
  const aiProcessing = useRecoilValue(curriculoProcessingAtom);
  const aiAnalysisDataFromAPI = useRecoilValue(curriculoAnalysisAtom);
  const suggestions = useRecoilValue(curriculoSuggestionsAtom);
  const loadingSuggestions = useRecoilValue(curriculoLoadingAtom);

  // Estados do Stepper
  const [activeStep, setActiveStep] = useState<number>(0);
  const [selectedAssessorado, setSelectedAssessorado] = useState<number | string>('');
  const [assessorados, setAssessorados] = useState<ComandaTemp[]>([]);
  const [loadingAssessorados, setLoadingAssessorados] = useState<boolean>(false);

  // Estados locais
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [editableData, setEditableData] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [hasProcessedFile, setHasProcessedFile] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string>('');
  const [newObj, setNewObj] = useState<any>({});
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState<boolean>(false);
  const [ignoredSuggestions, setIgnoredSuggestions] = useState<number[]>([]);
  const [salvarVersaoModalOpen, setSalvarVersaoModalOpen] = useState<boolean>(false);
  const [historicoDrawerOpen, setHistoricoDrawerOpen] = useState<boolean>(false);
  const [showUploadCard, setShowUploadCard] = useState<boolean>(true);

  // Labels do Stepper
  const steps = ['Selecionar Assessorado', 'Gerar Currículo com IA'];

  // Estrutura padrão para dados vazios
  const getDefaultCurriculoData = () => ({
    basics: {
      name: '',
      email: '',
      phone: '',
      website: '',
      resume: '',
      keywords: [],
      location: { address: '' },
    },
    education: [],
    work: [],
    skills: [],
    projects: [],
    qualifications: [],
    headings: {
      education: 'Formação Acadêmica',
      work: 'Experiência Profissional',
      skills: 'Competências Técnicas',
      projects: 'Projetos',
      qualifications: 'Qualificações',
    },
    sections: ['basics', 'education', 'work', 'skills', 'projects', 'qualifications'],
  });

  // Carregar assessorados
  const carregarAssessorados = async () => {
    try {
      setLoadingAssessorados(true);
      const query = '?Pagina=1&TamanhoPagina=1000'; // Buscar todos os assessorados
      const data = await comandaActions.list(false, query);
      const result = data.resultado.itens;
      setAssessorados(result as ComandaTemp[]);
    } catch (error) {
      toast.error('Erro ao carregar assessorados');
      console.error(error);
    } finally {
      setLoadingAssessorados(false);
    }
  };

  // Resetar formulário quando voltar ao step 0
  const resetFormulario = () => {
    setEditableData(null);
    setUploadedFile(null);
    setHasProcessedFile(false);
    setFileName('');
    setFileUrl('');
    setNewObj({});
    setCurrentTab(0);
    setIsSuggestionsPanelOpen(false);
    setIgnoredSuggestions([]);
    setShowUploadCard(true);
    setHistoricoDrawerOpen(false);
  };

  // Handlers do Stepper
  const handleNext = () => {
    if (activeStep === 0 && selectedAssessorado) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      resetFormulario();
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAssessoradoChange = (event: any) => {
    setSelectedAssessorado(event.target.value);
  };

  // Carregar assessorados na inicialização
  useEffect(() => {
    carregarAssessorados();
  }, []);

  // Sincronizar dados da API com estado editável
  useEffect(() => {
    if (aiAnalysisDataFromAPI) {
      const defaultData = getDefaultCurriculoData();
      const mergedData = {
        ...defaultData,
        ...aiAnalysisDataFromAPI,
        basics: { ...defaultData.basics, ...aiAnalysisDataFromAPI.basics },
        headings: { ...defaultData.headings, ...aiAnalysisDataFromAPI.headings },
      };
      setEditableData(mergedData);
    } else if (!editableData) {
      setEditableData(getDefaultCurriculoData());
    }
  }, [aiAnalysisDataFromAPI]);

  // Inicializar com dados padrão
  useEffect(() => {
    if (!editableData) {
      setEditableData(getDefaultCurriculoData());
    }
  }, []);

  // Observar mudanças no arquivo anexado pelo InputArquivo
  useEffect(() => {
    if (newObj.anexo && newObj.anexo instanceof File) {
      handleFileUpload(newObj.anexo);
    }
  }, [newObj.anexo]);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    setUploadedFile(file);
    setCurrentTab(0);

    setEditableData(getDefaultCurriculoData());

    curriculoActions.limparAnalise();
    curriculoActions.limparSugestoes();

    try {
      await curriculoActions.importarCurriculo({
        cvFile: file,
      });

      setHasProcessedFile(true);
      setShowUploadCard(false); // Fecha o accordion após upload
      toast.success(translate('curriculoProcessadoComSucesso'));
    } catch (error) {
      console.error('Erro ao processar CV:', error);
      toast.error(translate('erroAoProcessarCV'));
    }
  };

  const handleRequestSuggestions = async (
    secao:
      | 'basics'
      | 'education'
      | 'work'
      | 'skills'
      | 'projects'
      | 'qualifications'
      | 'headings'
      | 'sections',
  ) => {
    if (!editableData) return;

    try {
      await curriculoActions.obterSugestoesMelhoriaSecao({
        secao: secao,
        dadosCurriculo: editableData,
        contatoId: Number(auth?.id),
      });
    } catch (error) {
      console.error('Erro ao obter sugestões:', error);
      toast.error(translate('erroAoObterSugestoes'));
    }
  };

  const handleRequestFieldSuggestions = async (
    secao:
      | 'basics'
      | 'education'
      | 'work'
      | 'skills'
      | 'projects'
      | 'qualifications'
      | 'headings'
      | 'sections',
    caminhoCampo: string,
    valorAtual?: string,
  ) => {
    if (!editableData) return;

    try {
      await curriculoActions.obterSugestoesMelhoriaCampo({
        secao: secao,
        caminhoCampo: caminhoCampo,
        valorAtual: valorAtual,
        dadosCurriculo: editableData,
        contatoId: Number(auth?.id),
      });
    } catch (error) {
      console.error('Erro ao obter sugestões:', error);
      toast.error(translate('erroAoObterSugestoes'));
    }
  };

  // Abrir painel quando sugestões chegarem
  useEffect(() => {
    if (suggestions && suggestions.sugestoes && suggestions.sugestoes.length > 0) {
      const isCampoEspecifico =
        suggestions.caminhoCampo &&
        suggestions.caminhoCampo !== suggestions.secao &&
        /\[\d+\]/.test(suggestions.caminhoCampo);

      setIsSuggestionsPanelOpen(true);
      setIgnoredSuggestions([]); // Limpar sugestões ignoradas ao receber novas
    }
  }, [suggestions]);

  // Função para aplicar sugestão ao campo
  const handleApplySuggestion = (sugestaoIndex: number) => {
    if (!suggestions || !editableData) return;

    const sugestao = suggestions.sugestoes[sugestaoIndex];
    const { caminhoCampo, secao } = suggestions;

    // Verificar se é realmente um campo específico
    const isCampoEspecifico = caminhoCampo && caminhoCampo !== secao && caminhoCampo.trim() !== '';

    if (!isCampoEspecifico) {
      toast.info('Esta é uma sugestão geral. Aplique as mudanças manualmente.');
      return;
    }

    // Parse do caminhoCampo (ex: work[0].highlights[1], resume, keywords)
    try {
      setEditableData((prev: any) => {
        const newData = JSON.parse(JSON.stringify(prev)); // Deep clone

        // Para campos simples em basics (sem índices)
        if (secao === 'basics' && !caminhoCampo.includes('[') && !caminhoCampo.includes('.')) {
          // Campo simples em basics (ex: "resume", "name", "email")
          newData.basics[caminhoCampo] = sugestao.exemploMelhoria;
          return newData;
        }

        // Para campos simples com keywords (pode ser array)
        if (caminhoCampo === 'keywords' && secao === 'basics') {
          try {
            // Tentar parsear se for JSON string
            const parsedKeywords = JSON.parse(sugestao.exemploMelhoria);
            if (Array.isArray(parsedKeywords)) {
              newData.basics.keywords = parsedKeywords;
            } else {
              newData.basics.keywords = sugestao.exemploMelhoria;
            }
          } catch {
            // Se não for JSON, usar como está
            newData.basics.keywords = sugestao.exemploMelhoria;
          }
          return newData;
        }

        // Para caminhos complexos com índices (ex: work[0].highlights[1])
        const pathParts = caminhoCampo.split(/[[\].]/).filter(Boolean);

        let current = newData;
        for (let i = 0; i < pathParts.length - 1; i++) {
          const part = pathParts[i];
          const nextPart = pathParts[i + 1];

          if (!isNaN(Number(nextPart))) {
            // Next part is an index
            current = current[part];
          } else {
            // Next part is a property
            current = current[part];
          }
        }

        // Set the final value
        const lastKey = pathParts[pathParts.length - 1];

        if (!isNaN(Number(lastKey))) {
          // It's an array index
          current[Number(lastKey)] = sugestao.exemploMelhoria;
        } else {
          // It's a property
          current[lastKey] = sugestao.exemploMelhoria;
        }

        return newData;
      });

      // Marcar como ignorada (aplicada)
      setIgnoredSuggestions((prev) => [...prev, sugestaoIndex]);
      toast.success('Sugestão aplicada com sucesso!');
    } catch (error) {
      console.error('Erro ao aplicar sugestão:', error);
      toast.error('Erro ao aplicar sugestão. Tente manualmente.');
    }
  };

  // Função para ignorar sugestão
  const handleIgnoreSuggestion = (sugestaoIndex: number) => {
    setIgnoredSuggestions((prev) => [...prev, sugestaoIndex]);
    toast.info('Sugestão ignorada');
  };

  // Funções para adicionar/remover itens
  const handleAddEducation = () => {
    const newEducation = {
      institution: '',
      area: '',
      studyType: '',
      startDate: '',
      endDate: '',
      location: '',
    };
    setEditableData((prev: any) => ({
      ...prev,
      education: [...(prev?.education || []), newEducation],
    }));
  };

  const handleRemoveEducation = (index: number) => {
    setEditableData((prev: any) => ({
      ...prev,
      education: prev?.education?.filter((_: any, i: any) => i !== index) || [],
    }));
  };

  const handleAddWork = () => {
    const newWork = {
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      location: '',
      highlights: [''],
    };
    setEditableData((prev: any) => ({
      ...prev,
      work: [...(prev?.work || []), newWork],
    }));
  };

  const handleRemoveWork = (index: number) => {
    setEditableData((prev: any) => ({
      ...prev,
      work: prev?.work?.filter((_: any, i: any) => i !== index) || [],
    }));
  };

  const handleAddSkill = () => {
    const newSkill = {
      name: '',
      keywords: [''],
    };
    setEditableData((prev: any) => ({
      ...prev,
      skills: [...(prev?.skills || []), newSkill],
    }));
  };

  const handleRemoveSkill = (index: number) => {
    setEditableData((prev: any) => ({
      ...prev,
      skills: prev?.skills?.filter((_: any, i: any) => i !== index) || [],
    }));
  };

  const handleAddProject = () => {
    const newProject = {
      name: '',
      description: '',
      url: '',
      highlights: [''],
    };
    setEditableData((prev: any) => ({
      ...prev,
      projects: [...(prev?.projects || []), newProject],
    }));
  };

  const handleRemoveProject = (index: number) => {
    setEditableData((prev: any) => ({
      ...prev,
      projects: prev?.projects?.filter((_: any, i: any) => i !== index) || [],
    }));
  };

  const handleAddQualification = () => {
    const newQualification = {
      title: '',
    };
    setEditableData((prev: any) => ({
      ...prev,
      qualifications: [...(prev?.qualifications || []), newQualification],
    }));
  };

  const handleRemoveQualification = (index: number) => {
    setEditableData((prev: any) => ({
      ...prev,
      qualifications: prev?.qualifications?.filter((_: any, i: any) => i !== index) || [],
    }));
  };

  const handleFieldChange = (section: string, index: number | null, field: string, value: any) => {
    setEditableData((prev: any) => {
      if (index === null) {
        return {
          ...prev,
          [section]: {
            ...prev?.[section],
            [field]: value,
          },
        };
      } else {
        const updatedArray = [...(prev?.[section] || [])];
        updatedArray[index] = {
          ...updatedArray[index],
          [field]: value,
        };
        return {
          ...prev,
          [section]: updatedArray,
        };
      }
    });
  };

  // Componente de Painel Lateral de Sugestões com Overlay
  const SuggestionsSidebar = () => {
    if (!suggestions) return null;

    // Verificar se é sugestão de campo específico
    // Considera específico se caminhoCampo existe e é diferente da seção
    const isCampoEspecifico =
      suggestions.caminhoCampo &&
      suggestions.caminhoCampo !== suggestions.secao &&
      suggestions.caminhoCampo.trim() !== '';

    // Filtrar apenas sugestões que foram ignoradas/aplicadas (só para campos específicos)
    const activeSuggestions = isCampoEspecifico
      ? suggestions.sugestoes?.filter((_, index) => !ignoredSuggestions.includes(index)) || []
      : suggestions.sugestoes || []; // Para seção completa, mostra todas

    return (
      <>
        {/* Overlay Background */}
        {isSuggestionsPanelOpen && (
          <Box
            onClick={() => setIsSuggestionsPanelOpen(false)}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 1200,
              transition: 'opacity 0.3s',
            }}
          />
        )}

        {/* Sidebar Panel */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: isSuggestionsPanelOpen ? 0 : '-420px',
            bottom: 0,
            width: '420px',
            bgcolor: 'background.paper',
            boxShadow: '4px 0 20px rgba(0,0,0,0.15)',
            zIndex: 1300,
            transition: 'left 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <LightbulbIcon />
              <Typography variant="h6" fontWeight="bold" color="white">
                Sugestões de IA
              </Typography>
            </Box>
            <Chip
              label={`${suggestions.pontuacaoGeral}/100`}
              size="small"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 'bold',
              }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Info Card */}
            <Card
              sx={{ mb: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.light' }}
            >
              <CardContent>
                <Typography variant="subtitle2" fontWeight="bold" color="info.main" gutterBottom>
                  {suggestions.descricaoCampo}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {suggestions.resumoAnalise}
                </Typography>
              </CardContent>
            </Card>

            {/* Loading State */}
            {loadingSuggestions && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}

            {/* Sugestões */}
            {activeSuggestions.length === 0 && !loadingSuggestions && isCampoEspecifico ? (
              <Box textAlign="center" py={4}>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Todas as sugestões foram processadas!
                </Typography>
              </Box>
            ) : (
              activeSuggestions.map((sugestao: any, index: number) => {
                const originalIndex = suggestions.sugestoes.indexOf(sugestao);
                return (
                  <Card
                    key={originalIndex}
                    sx={{
                      mb: 2,
                      border: '1px solid',
                      borderColor:
                        sugestao.tipo === 'Erro'
                          ? 'error.light'
                          : sugestao.tipo === 'Conteúdo' || sugestao.tipo === 'Estrutura'
                            ? 'warning.light'
                            : 'info.light',
                    }}
                  >
                    <CardContent>
                      {/* Header com Tipo e Prioridade */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Chip
                          label={sugestao.tipo}
                          color={
                            sugestao.tipo === 'Gramática' || sugestao.tipo === 'Erro'
                              ? 'error'
                              : sugestao.tipo === 'Conteúdo' || sugestao.tipo === 'Estrutura'
                                ? 'warning'
                                : sugestao.tipo === 'ATS'
                                  ? 'success'
                                  : 'info'
                          }
                          size="small"
                        />
                        <Box display="flex" alignItems="center">
                          {Array.from({ length: 5 }, (_, i) => (
                            <span
                              key={i}
                              style={{ color: i < sugestao.prioridade ? '#FFB400' : '#E0E0E0' }}
                            >
                              ★
                            </span>
                          ))}
                        </Box>
                      </Box>

                      {/* Título */}
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {sugestao.titulo}
                      </Typography>

                      {/* Descrição */}
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {sugestao.descricao}
                      </Typography>

                      {/* Exemplo de Melhoria */}
                      {sugestao.exemploMelhoria && (
                        <Box
                          sx={{
                            bgcolor: 'grey.50',
                            p: 1.5,
                            borderRadius: 1,
                            mb: 2,
                            border: '1px dashed',
                            borderColor: 'grey.300',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            💡 Exemplo de melhoria:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}
                          >
                            {sugestao.exemploMelhoria}
                          </Typography>
                        </Box>
                      )}

                      {/* Botões de Ação - Apenas para campos específicos */}
                      {isCampoEspecifico && (
                        <Box display="flex" gap={1}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApplySuggestion(originalIndex)}
                            fullWidth
                            color="success"
                          >
                            Aplicar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={() => handleIgnoreSuggestion(originalIndex)}
                            fullWidth
                            color="error"
                          >
                            Ignorar
                          </Button>
                        </Box>
                      )}

                      {/* Mensagem informativa para sugestões de seção completa */}
                      {!isCampoEspecifico && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            bgcolor: 'info.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'info.light',
                          }}
                        >
                          <Typography variant="caption" color="info.main">
                            ℹ️ Sugestão de seção completa - Aplique as mudanças manualmente nos
                            campos
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Box>

          {/* Footer - Botão para fechar */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button variant="outlined" fullWidth onClick={() => setIsSuggestionsPanelOpen(false)}>
              Fechar Painel
            </Button>
          </Box>
        </Box>

        {/* Toggle Button - Visível quando há sugestões */}
        {!isSuggestionsPanelOpen && activeSuggestions.length > 0 && (
          <Box
            onClick={() => setIsSuggestionsPanelOpen(true)}
            sx={{
              position: 'fixed',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'primary.main',
              color: 'white',
              p: 1,
              cursor: 'pointer',
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8,
              boxShadow: '2px 2px 8px rgba(0,0,0,0.2)',
              zIndex: 1100,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'primary.dark',
                pl: 1.5,
              },
            }}
          >
            <ChevronRightIcon />
            <Box>
              <Typography variant="caption" fontWeight="bold" display="block">
                Sugestões da STATO IA
              </Typography>
              {isCampoEspecifico ? (
                <Chip
                  label={activeSuggestions.length}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    color: 'primary.main',
                  }}
                />
              ) : (
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  Seção completa
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </>
    );
  };

  // Função para preview do CV
  const handlePreviewCV = async () => {
    if (!editableData) {
      toast.error('Nenhum dado disponível para preview');
      return;
    }

    try {
      toast.info('Gerando preview...');

      // Chama a API para gerar o HTML do currículo
      const result = await curriculoActions.gerarCVAtualizado(editableData);

      if (!result || typeof result !== 'string') {
        throw new Error('Formato de resposta inválido da API');
      }

      const nomeUsuario = editableData?.basics?.name || 'curriculo';

      // Abre apenas o preview
      previewCurriculumHTML(result, nomeUsuario);
      toast.success('Preview aberto em nova janela!');
    } catch (error: any) {
      console.error('Erro ao gerar preview:', error);
      toast.error(error?.message || 'Erro ao gerar preview. Tente novamente.');
    }
  };

  // Função para gerar CV atualizado (PDF)
  const handleGerarCVAtualizado = async () => {
    if (!editableData) {
      toast.error('Nenhum dado disponível para gerar o CV');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info('Gerando currículo...');

      // Chama a API para gerar o HTML do currículo
      const result = await curriculoActions.gerarCVAtualizado(editableData);

      if (!result || typeof result !== 'string') {
        throw new Error('Formato de resposta inválido da API');
      }

      const nomeUsuario = editableData?.basics?.name || 'curriculo';

      // Gera o PDF diretamente
      toast.info('Gerando PDF...');
      await generatePDFFromHTML(result, nomeUsuario);

      toast.success('PDF gerado e baixado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao gerar CV:', error);
      toast.error(error?.message || 'Erro ao gerar currículo. Tente novamente.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <SmartToyIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {translate('novoCurriculoIA')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {translate('crieSeucurriculoComAjudaDaIA')}
            </Typography>
          </Box>
        </Box>

        {/* Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step 0 - Seleção de Assessorado */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Selecione o Assessorado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Escolha o assessorado para o qual deseja gerar o currículo com IA
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Assessorado</InputLabel>
              <SelectStato
                value={selectedAssessorado}
                onChange={handleAssessoradoChange}
                label="Assessorado"
                disabled={loadingAssessorados}
              >
                {assessorados.map((assessorado) => (
                  <MenuItem key={assessorado.id} value={assessorado.idContato}>
                    {assessorado.nomeContato} - {assessorado.emailContato}
                  </MenuItem>
                ))}
              </SelectStato>
              {loadingAssessorados && (
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Carregando assessorados...
                  </Typography>
                </Box>
              )}
            </FormControl>

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleNext} disabled={!selectedAssessorado}>
                Próximo
                <ChevronRightIcon />
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 1 - Formulário de Currículo */}
        {activeStep === 1 && (
          <>
            {/* Botão Voltar */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Button startIcon={<ChevronLeftIcon />} onClick={handleBack}>
                Voltar
              </Button>
              <Typography variant="body2" color="text.secondary">
                Assessorado selecionado:{' '}
                {assessorados.find((a) => a.id === selectedAssessorado)?.nomeContato}
              </Typography>
            </Box>

            {/* Upload de Arquivo - Colapsável */}
            <Accordion
              expanded={showUploadCard}
              onChange={() => setShowUploadCard(!showUploadCard)}
              sx={{ mb: 3, border: '1px solid', borderColor: 'primary.main' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'primary.50',
                  '& .MuiAccordionSummary-content': { alignItems: 'center' },
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <CloudUploadIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    {translate('envieSeucurriculo')}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {translate('envieSeucurriculoParaAnaliseIA')}
                  </Typography>

                  <InputArquivo
                    acceptTypes=".pdf,.doc,.docx"
                    isEdit={false}
                    newObj={newObj}
                    setNewObj={setNewObj}
                    nameFile={fileName}
                    setNameFile={setFileName}
                    urlFile={fileUrl}
                    setUrlFile={setFileUrl}
                  />

                  {aiProcessing && (
                    <Box sx={{ mt: 3 }}>
                      <LinearProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        {translate('processandoCurriculo')}
                      </Typography>
                    </Box>
                  )}

                  {/* Informações do arquivo processado */}
                  {uploadedFile && (
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        {translate('arquivoProcessado')}:
                      </Typography>
                      <Chip label={uploadedFile.name} color="success" icon={<SmartToyIcon />} />
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Informação sobre criar do zero */}
            {!uploadedFile && (
              <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Dica:</strong> Você pode criar seu currículo do zero preenchendo o
                  formulário abaixo, ou usar o accordion acima para importar um currículo existente.
                </Typography>
              </Alert>
            )}

            {hasProcessedFile && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ✅ Currículo processado com sucesso! Use as abas abaixo para editar e solicitar
                  sugestões da IA.
                </Typography>
              </Alert>
            )}

            {/* Abas do Formulário */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={currentTab}
                onChange={(_, newValue) => setCurrentTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={translate('informacoesBasicas')} />
                <Tab label={translate('formacaoAcademica')} />
                <Tab label={translate('experienciaProfissional')} />
                <Tab label={translate('habilidades')} />
                <Tab label={translate('projetos')} />
                <Tab label={translate('qualificacoes')} />
              </Tabs>
            </Box>

            {/* Conteúdo das Abas */}
            <Box sx={{ minHeight: 400 }}>
              {/* Tab 0 - Informações Básicas */}
              {currentTab === 0 && (
                <Box>
                  <Box display="flex" justifyContent="flex-end" mb={2}>
                    <Button
                      onClick={() => handleRequestSuggestions('basics')}
                      disabled={loadingSuggestions}
                      startIcon={loadingSuggestions ? <CircularProgress size={16} /> : null}
                    >
                      💡 {translate('pedirSugestoes')}
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" gap={1} alignItems="flex-end">
                        <TextFieldStato
                          fullWidth
                          label={translate('nomeCompleto')}
                          value={editableData?.basics?.name || ''}
                          onChange={(e) =>
                            handleFieldChange('basics', null, 'name', e.target.value)
                          }
                          placeholder="Seu nome completo"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" gap={1} alignItems="flex-end">
                        <TextFieldStato
                          fullWidth
                          label={translate('email')}
                          value={editableData?.basics?.email || ''}
                          onChange={(e) =>
                            handleFieldChange('basics', null, 'email', e.target.value)
                          }
                          placeholder="seu.email@exemplo.com"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" gap={1} alignItems="flex-end">
                        <TextFieldStato
                          fullWidth
                          label={translate('telefone')}
                          value={editableData?.basics?.phone || ''}
                          onChange={(e: any) =>
                            handleFieldChange('basics', null, 'phone', e.target.value)
                          }
                          placeholder="(11) 99999-9999"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" gap={1} alignItems="flex-end">
                        <TextFieldStato
                          fullWidth
                          label={translate('website')}
                          value={editableData?.basics?.website || ''}
                          onChange={(e) =>
                            handleFieldChange('basics', null, 'website', e.target.value)
                          }
                          placeholder="https://seusite.com"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextFieldStato
                        fullWidth
                        label={translate('localizacao')}
                        value={editableData?.basics?.location?.address || ''}
                        onChange={(e) =>
                          handleFieldChange('basics', null, 'location', { address: e.target.value })
                        }
                        placeholder="Cidade, Estado, País"
                      />
                    </Grid>

                    {/* NOVO: Campo Resume */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Resumo Profissional
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <TextFieldStato
                          fullWidth
                          label="Resumo Profissional (2-4 linhas)"
                          value={editableData?.basics?.resume || ''}
                          onChange={(e) =>
                            handleFieldChange('basics', null, 'resume', e.target.value)
                          }
                          placeholder="Descreva suas principais conquistas e experiências em 2-4 linhas. Seja conciso e impactante, destaque resultados quantificáveis."
                          multiline
                          rows={3}
                          inputProps={{ maxLength: 500 }}
                        />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            💡 Dica: Seja conciso, use números e destaque suas principais conquistas
                          </Typography>
                          <Box display="flex" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                              {editableData?.basics?.resume?.length || 0}/500
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                handleRequestFieldSuggestions(
                                  'basics',
                                  'resume',
                                  editableData?.basics?.resume,
                                )
                              }
                              disabled={loadingSuggestions || !editableData?.basics?.resume}
                            >
                              💡 Analisar
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    {/* NOVO: Campo Keywords (ATS) */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Palavras-chave (ATS)
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" gap={1} mb={1}>
                          <TextFieldStato
                            fullWidth
                            label="Adicionar palavra-chave"
                            placeholder="Digite uma tecnologia, skill ou ferramenta (ex: NodeJS, React, AWS)"
                            onKeyPress={(e: any) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const currentKeywords = editableData?.basics?.keywords || [];
                                if (!currentKeywords.includes(e.target.value.trim())) {
                                  handleFieldChange('basics', null, 'keywords', [
                                    ...currentKeywords,
                                    e.target.value.trim(),
                                  ]);
                                  e.target.value = '';
                                }
                              }
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={() =>
                              handleRequestFieldSuggestions(
                                'basics',
                                'keywords',
                                JSON.stringify(editableData?.basics?.keywords || []),
                              )
                            }
                            disabled={loadingSuggestions || !editableData?.basics?.keywords?.length}
                            sx={{ minWidth: '120px' }}
                          >
                            💡 Analisar
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          💡 Dica: Adicione tecnologias e skills relevantes. Importante para
                          sistemas ATS (Applicant Tracking System)
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {(editableData?.basics?.keywords || []).map(
                            (keyword: string, index: number) => (
                              <Chip
                                key={index}
                                label={keyword}
                                onDelete={() => {
                                  const newKeywords = (editableData?.basics?.keywords || []).filter(
                                    (_: string, i: number) => i !== index,
                                  );
                                  handleFieldChange('basics', null, 'keywords', newKeywords);
                                }}
                                color="primary"
                                variant="outlined"
                              />
                            ),
                          )}
                          {(!editableData?.basics?.keywords ||
                            editableData.basics.keywords.length === 0) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}
                            >
                              Nenhuma palavra-chave adicionada. Pressione Enter após digitar para
                              adicionar.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 1 - Formação Acadêmica */}
              {currentTab === 1 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{translate('formacaoAcademica')}</Typography>
                    <Box display="flex" gap={1}>
                      <Button variant="contained" onClick={handleAddEducation} color="primary">
                        + {translate('adicionarFormacao')}
                      </Button>
                      <Button
                        onClick={() => handleRequestSuggestions('education')}
                        disabled={loadingSuggestions}
                        startIcon={loadingSuggestions ? <CircularProgress size={16} /> : null}
                      >
                        💡 {translate('pedirSugestoes')}
                      </Button>
                    </Box>
                  </Box>

                  {editableData?.education?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Nenhuma formação acadêmica adicionada. Clique em "+{' '}
                        {translate('adicionarFormacao')}" para começar.
                      </Typography>
                    </Box>
                  )}

                  {editableData?.education?.map((edu: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button color="error" onClick={() => handleRemoveEducation(index)}>
                              - {translate('remover')}
                            </Button>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box display="flex" gap={1} alignItems="flex-end">
                              <TextFieldStato
                                fullWidth
                                label={translate('instituicao')}
                                value={edu.institution || ''}
                                onChange={(e) =>
                                  handleFieldChange(
                                    'education',
                                    index,
                                    'institution',
                                    e.target.value,
                                  )
                                }
                                placeholder="Nome da instituição"
                              />
                              <Button
                                variant="outlined"
                                onClick={() =>
                                  handleRequestFieldSuggestions(
                                    'education',
                                    `education[${index}].institution`,
                                    edu.institution,
                                  )
                                }
                                disabled={loadingSuggestions || !edu.institution}
                                sx={{
                                  minWidth: '56px',
                                  height: '56px',
                                  px: 1,
                                }}
                              >
                                💡
                              </Button>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextFieldStato
                              fullWidth
                              label={translate('areadeEstudo')}
                              value={edu.area || ''}
                              onChange={(e) =>
                                handleFieldChange('education', index, 'area', e.target.value)
                              }
                              placeholder="Área de estudo"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextFieldStato
                              fullWidth
                              label={translate('tipoCurso')}
                              value={edu.studyType || ''}
                              onChange={(e) =>
                                handleFieldChange('education', index, 'studyType', e.target.value)
                              }
                              placeholder="Bacharelado, Mestrado, etc."
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextFieldStato
                              fullWidth
                              label={translate('dataInicio')}
                              value={edu.startDate || ''}
                              onChange={(e) =>
                                handleFieldChange('education', index, 'startDate', e.target.value)
                              }
                              placeholder="YYYY-MM"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextFieldStato
                              fullWidth
                              label={translate('dataConclusao')}
                              value={edu.endDate || ''}
                              onChange={(e) =>
                                handleFieldChange('education', index, 'endDate', e.target.value)
                              }
                              placeholder="YYYY-MM ou 'Cursando'"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextFieldStato
                              fullWidth
                              label={translate('localizacao')}
                              value={edu.location || ''}
                              onChange={(e) =>
                                handleFieldChange('education', index, 'location', e.target.value)
                              }
                              placeholder="Cidade, Estado"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* Tab 2 - Experiência Profissional */}
              {currentTab === 2 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{translate('experienciaProfissional')}</Typography>
                    <Box display="flex" gap={1}>
                      <Button variant="contained" onClick={handleAddWork} color="primary">
                        + {translate('adicionarExperiencia')}
                      </Button>
                      <Button
                        onClick={() => handleRequestSuggestions('work')}
                        disabled={loadingSuggestions}
                        startIcon={loadingSuggestions ? <CircularProgress size={16} /> : null}
                      >
                        💡 {translate('pedirSugestoes')}
                      </Button>
                    </Box>
                  </Box>

                  {editableData?.work?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Nenhuma experiência profissional adicionada. Clique em "+{' '}
                        {translate('adicionarExperiencia')}" para começar.
                      </Typography>
                    </Box>
                  )}

                  {editableData?.work?.map((work: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button color="error" onClick={() => handleRemoveWork(index)}>
                              - {translate('remover')}
                            </Button>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextFieldStato
                              fullWidth
                              label={translate('empresa')}
                              value={work.company || ''}
                              onChange={(e) =>
                                handleFieldChange('work', index, 'company', e.target.value)
                              }
                              placeholder="Nome da empresa"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextFieldStato
                              fullWidth
                              label={translate('cargo')}
                              value={work.position || ''}
                              onChange={(e) =>
                                handleFieldChange('work', index, 'position', e.target.value)
                              }
                              placeholder="Seu cargo na empresa"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextFieldStato
                              fullWidth
                              label={translate('dataInicio')}
                              value={work.startDate || ''}
                              onChange={(e) =>
                                handleFieldChange('work', index, 'startDate', e.target.value)
                              }
                              placeholder="YYYY-MM"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextFieldStato
                              fullWidth
                              label={translate('dataSaida')}
                              value={work.endDate || ''}
                              onChange={(e) =>
                                handleFieldChange('work', index, 'endDate', e.target.value)
                              }
                              placeholder="YYYY-MM ou 'Atual'"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextFieldStato
                              fullWidth
                              label={translate('localizacao')}
                              value={work.location || ''}
                              onChange={(e) =>
                                handleFieldChange('work', index, 'location', e.target.value)
                              }
                              placeholder="Cidade, Estado"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="subtitle2">
                                {translate('principaisRealizacoes')}
                              </Typography>
                              <Button
                                onClick={() => {
                                  const newHighlights = [...(work.highlights || []), ''];
                                  handleFieldChange('work', index, 'highlights', newHighlights);
                                }}
                              >
                                + {translate('adicionarHighlight')}
                              </Button>
                            </Box>
                            {(work.highlights || ['']).map((highlight: string, hIndex: number) => (
                              <Box key={hIndex} mb={1}>
                                <Box display="flex" gap={1}>
                                  <TextFieldStato
                                    fullWidth
                                    value={highlight}
                                    onChange={(e) => {
                                      const newHighlights = [...(work.highlights || [])];
                                      newHighlights[hIndex] = e.target.value;
                                      handleFieldChange('work', index, 'highlights', newHighlights);
                                    }}
                                    placeholder="Descreva uma realização ou responsabilidade"
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleRequestFieldSuggestions(
                                        'work',
                                        `work[${index}].highlights[${hIndex}]`,
                                        highlight,
                                      )
                                    }
                                    disabled={loadingSuggestions || !highlight}
                                    sx={{ minWidth: '40px', px: 1 }}
                                  >
                                    💡
                                  </Button>
                                  {(work.highlights || []).length > 1 && (
                                    <Button
                                      color="error"
                                      size="small"
                                      onClick={() => {
                                        const newHighlights = work.highlights.filter(
                                          (_: any, i: number) => i !== hIndex,
                                        );
                                        handleFieldChange(
                                          'work',
                                          index,
                                          'highlights',
                                          newHighlights,
                                        );
                                      }}
                                      sx={{ minWidth: '40px' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* Tab 3 - Habilidades */}
              {currentTab === 3 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{translate('habilidades')}</Typography>
                    <Box display="flex" gap={1}>
                      <Button variant="contained" onClick={handleAddSkill} color="primary">
                        + {translate('adicionarCategoria')}
                      </Button>
                      <Button
                        onClick={() => handleRequestSuggestions('skills')}
                        disabled={loadingSuggestions}
                        startIcon={loadingSuggestions ? <CircularProgress size={16} /> : null}
                      >
                        💡 {translate('pedirSugestoes')}
                      </Button>
                    </Box>
                  </Box>

                  {editableData?.skills?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Nenhuma habilidade adicionada. Clique em "+{' '}
                        {translate('adicionarCategoria')}" para começar.
                      </Typography>
                    </Box>
                  )}

                  {editableData?.skills?.map((skill: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button color="error" onClick={() => handleRemoveSkill(index)}>
                              - {translate('remover')}
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <TextFieldStato
                              fullWidth
                              label={translate('categoriaHabilidade')}
                              value={skill.name || ''}
                              onChange={(e) =>
                                handleFieldChange('skills', index, 'name', e.target.value)
                              }
                              placeholder="ex: Linguagens de Programação"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="subtitle2">
                                {translate('habilidadesEspecificas')}
                              </Typography>
                              <Button
                                onClick={() => {
                                  const newKeywords = [...(skill.keywords || []), ''];
                                  handleFieldChange('skills', index, 'keywords', newKeywords);
                                }}
                              >
                                + {translate('adicionarKeyword')}
                              </Button>
                            </Box>
                            {(skill.keywords || ['']).map((keyword: string, kIndex: number) => (
                              <Box key={kIndex} display="flex" gap={1} mb={1}>
                                <TextFieldStato
                                  fullWidth
                                  value={keyword}
                                  onChange={(e) => {
                                    const newKeywords = [...(skill.keywords || [])];
                                    newKeywords[kIndex] = e.target.value;
                                    handleFieldChange('skills', index, 'keywords', newKeywords);
                                  }}
                                  placeholder="ex: JavaScript, Python, React"
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    handleRequestFieldSuggestions(
                                      'skills',
                                      `skills[${index}].keywords[${kIndex}]`,
                                      keyword,
                                    )
                                  }
                                  disabled={loadingSuggestions || !keyword}
                                  sx={{ minWidth: '40px', px: 1 }}
                                >
                                  💡
                                </Button>
                                {(skill.keywords || []).length > 1 && (
                                  <Button
                                    color="error"
                                    size="small"
                                    onClick={() => {
                                      const newKeywords = skill.keywords.filter(
                                        (_: any, i: number) => i !== kIndex,
                                      );
                                      handleFieldChange('skills', index, 'keywords', newKeywords);
                                    }}
                                    sx={{ minWidth: '40px' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </Button>
                                )}
                              </Box>
                            ))}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* Tab 4 - Projetos */}
              {currentTab === 4 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{translate('projetos')}</Typography>
                    <Box display="flex" gap={1}>
                      <Button variant="contained" onClick={handleAddProject} color="primary">
                        + {translate('adicionarProjeto')}
                      </Button>
                      <Button
                        onClick={() => handleRequestSuggestions('projects')}
                        disabled={loadingSuggestions}
                        startIcon={loadingSuggestions ? <CircularProgress size={16} /> : null}
                      >
                        💡 {translate('pedirSugestoes')}
                      </Button>
                    </Box>
                  </Box>

                  {editableData?.projects?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Nenhum projeto adicionado. Clique em "+ {translate('adicionarProjeto')}"
                        para começar.
                      </Typography>
                    </Box>
                  )}

                  {editableData?.projects?.map((project: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button color="error" onClick={() => handleRemoveProject(index)}>
                              - {translate('remover')}
                            </Button>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextFieldStato
                              fullWidth
                              label={translate('titulo')}
                              value={project.name || ''}
                              onChange={(e) =>
                                handleFieldChange('projects', index, 'name', e.target.value)
                              }
                              placeholder="Nome do projeto"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextFieldStato
                              fullWidth
                              label={translate('url')}
                              value={project.url || ''}
                              onChange={(e) =>
                                handleFieldChange('projects', index, 'url', e.target.value)
                              }
                              placeholder="https://..."
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextFieldStato
                              fullWidth
                              label={translate('descricao')}
                              value={project.description || ''}
                              onChange={(e) =>
                                handleFieldChange('projects', index, 'description', e.target.value)
                              }
                              multiline
                              rows={3}
                              placeholder="Descreva o projeto, tecnologias utilizadas e resultados"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* Tab 5 - Qualificações */}
              {currentTab === 5 && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{translate('certificacoesEQualificacoes')}</Typography>
                    <Box display="flex" gap={1}>
                      <Button variant="contained" onClick={handleAddQualification} color="primary">
                        + Adicionar Qualificação
                      </Button>
                      <Button
                        onClick={() => handleRequestSuggestions('qualifications')}
                        disabled={loadingSuggestions}
                        startIcon={loadingSuggestions ? <CircularProgress size={16} /> : null}
                      >
                        💡 {translate('pedirSugestoes')}
                      </Button>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Qualificações e Certificações:</strong> Adicione suas certificações,
                      cursos complementares e qualificações relevantes para sua área. Exemplos: AWS
                      Certified Solutions Architect, Scrum Master Certification, MBA em Gestão de
                      Projetos.
                    </Typography>
                  </Alert>

                  {editableData?.qualifications?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Nenhuma qualificação adicionada. Clique em "+ Adicionar Qualificação" para
                        começar.
                      </Typography>
                    </Box>
                  )}

                  {editableData?.qualifications?.map((qualification: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button color="error" onClick={() => handleRemoveQualification(index)}>
                              - {translate('remover')}
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <Box display="flex" gap={1} alignItems="flex-end">
                              <TextFieldStato
                                fullWidth
                                label="Título da Qualificação/Certificação"
                                value={qualification.title || ''}
                                onChange={(e) =>
                                  handleFieldChange(
                                    'qualifications',
                                    index,
                                    'title',
                                    e.target.value,
                                  )
                                }
                                placeholder="Ex: AWS Certified Solutions Architect, Scrum Master Certification (CSM)"
                              />
                              <Button
                                variant="outlined"
                                onClick={() =>
                                  handleRequestFieldSuggestions(
                                    'qualifications',
                                    `qualifications[${index}].title`,
                                    qualification.title,
                                  )
                                }
                                disabled={loadingSuggestions || !qualification.title}
                                sx={{
                                  minWidth: '56px',
                                  height: '56px',
                                  px: 1,
                                }}
                              >
                                💡
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>

            {/* Botões de Ação */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}
            >
              <Button variant="outlined" onClick={handleBack}>
                Voltar para Seleção
              </Button>

              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoricoDrawerOpen(true)}
                  disabled={!auth?.id || !selectedAssessorado}
                >
                  Ver Histórico
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={() => setSalvarVersaoModalOpen(true)}
                  disabled={!editableData || !auth?.id}
                >
                  Salvar Rascunho
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<VisibilityIcon />}
                  onClick={handlePreviewCV}
                  disabled={!editableData || isGeneratingPDF || !selectedAssessorado}
                >
                  Preview
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    isGeneratingPDF ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <AutoFixHighIcon />
                    )
                  }
                  onClick={handleGerarCVAtualizado}
                  disabled={!editableData || isGeneratingPDF}
                >
                  {isGeneratingPDF ? 'Gerando PDF...' : translate('gerarCVAtualizado')}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Painel Lateral de Sugestões */}
      <SuggestionsSidebar />

      {/* Modal Salvar Versão */}
      <SalvarVersaoCurriculoModal
        open={salvarVersaoModalOpen}
        onClose={() => setSalvarVersaoModalOpen(false)}
        contatoId={selectedAssessorado ? Number(selectedAssessorado) : Number(auth?.id)}
        curriculoData={editableData}
        onSaveSuccess={() => {
          // Atualizar lista de histórico se estiver aberta
          if (historicoDrawerOpen) {
            setHistoricoDrawerOpen(false);
            setTimeout(() => setHistoricoDrawerOpen(true), 100);
          }
        }}
      />

      {/* Drawer Histórico */}
      <HistoricoCurriculos
        open={historicoDrawerOpen}
        onClose={() => setHistoricoDrawerOpen(false)}
        contatoId={selectedAssessorado ? Number(selectedAssessorado) : Number(auth?.id)}
        onRestoreVersion={(curriculoData) => {
          setEditableData(curriculoData);
          setShowUploadCard(false); // Fecha o accordion ao restaurar
        }}
      />
    </Container>
  );
};

export default NovoCurriculoIAComponent;
